import React, { useState } from 'react';
import { X, Building, ArrowRight, Globe, FileText, Shield } from 'lucide-react';
interface AddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClientCreated: () => void;
  onClientCreatedThenAddAccount: () => void;
  orgName?: string;
}
export function AddClientModal({
  isOpen,
  onClose,
  onClientCreated,
  onClientCreatedThenAddAccount,
  orgName = 'My Organization'
}: AddClientModalProps) {
  const [isInternal, setIsInternal] = useState(false);
  const [clientName, setClientName] = useState('');
  const [industry, setIndustry] = useState('');
  const [notes, setNotes] = useState('');
  if (!isOpen) return null;
  const handleToggleInternal = (value: boolean) => {
    setIsInternal(value);
    if (value) {
      setClientName(orgName);
      setNotes('Internal infrastructure — our own environment.');
    } else {
      setClientName('');
      setNotes('');
    }
  };
  const handleCreateAndDone = () => {
    setClientName('');
    setIndustry('');
    setNotes('');
    setIsInternal(false);
    onClientCreated();
  };
  const handleCreateAndAddAccount = () => {
    setClientName('');
    setIndustry('');
    setNotes('');
    setIsInternal(false);
    onClientCreatedThenAddAccount();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-66">
      <div className="w-full max-w-[500px] bg-astro-card border border-astro-border rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-67">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-astro-border" data-id="element-68">
          <div data-id="element-69">
            <h2 className="text-lg font-bold text-white" data-id="element-70">Add a Client</h2>
            <p className="text-xs text-astro-text-secondary mt-0.5" data-id="element-71">
              Clients represent the organizations you manage security for.
            </p>
          </div>
          <button onClick={onClose} className="text-astro-text-secondary hover:text-white transition-colors p-1" data-id="element-72">

            <X size={20} data-id="element-73" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5" data-id="element-74">
          {/* Internal toggle */}
          <div className="flex gap-2" data-id="element-75">
            <button onClick={() => handleToggleInternal(false)} className={`flex-1 py-2.5 px-3 text-sm font-medium rounded border transition-all flex items-center justify-center gap-2 ${!isInternal ? 'bg-astro-green/10 border-astro-green text-white' : 'bg-[#0a0e14] border-astro-border text-astro-text-secondary hover:text-white hover:border-white/20'}`} data-id="element-76">

              <Building size={15} data-id="element-77" />
              External Client
            </button>
            <button onClick={() => handleToggleInternal(true)} className={`flex-1 py-2.5 px-3 text-sm font-medium rounded border transition-all flex items-center justify-center gap-2 ${isInternal ? 'bg-blue-500/10 border-blue-500/50 text-blue-400' : 'bg-[#0a0e14] border-astro-border text-astro-text-secondary hover:text-white hover:border-white/20'}`} data-id="element-78">

              <Shield size={15} data-id="element-79" />
              Our Own Infra
            </button>
          </div>

          {isInternal && <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 flex items-start gap-2.5" data-id="element-80">
              <Shield size={14} className="text-blue-400 mt-0.5 shrink-0" data-id="element-81" />
              <p className="text-[11px] text-blue-300/80 leading-relaxed" data-id="element-82">
                This creates a client record for your own infrastructure. Use it
                to test scans, validate configurations, or monitor your internal
                environment — all using the same workflow as your external
                clients.
              </p>
            </div>}

          {/* Client Name */}
          <div data-id="element-83">
            <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-84">
              {isInternal ? 'Display Name' : 'Client Name'}
            </label>
            <div className="relative" data-id="element-85">
              <Building size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-astro-text-secondary" data-id="element-86" />

              <input type="text" placeholder={isInternal ? 'e.g. Internal Lab' : 'e.g. Acme Corp'} value={clientName} onChange={e => setClientName(e.target.value)} className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2.5 pl-9 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors" autoFocus data-id="element-87" />

            </div>
          </div>

          {/* Industry — only for external clients */}
          {!isInternal && <div data-id="element-88">
              <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-89">
                Industry
                <span className="text-astro-text-secondary/50 font-normal ml-1" data-id="element-90">
                  (optional)
                </span>
              </label>
              <div className="relative" data-id="element-91">
                <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-astro-text-secondary" data-id="element-92" />

                <select value={industry} onChange={e => setIndustry(e.target.value)} className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2.5 pl-9 text-sm text-white appearance-none focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-93">

                  <option value="" data-id="element-94">Select industry…</option>
                  <option value="finance" data-id="element-95">Financial Services</option>
                  <option value="healthcare" data-id="element-96">Healthcare</option>
                  <option value="technology" data-id="element-97">Technology</option>
                  <option value="manufacturing" data-id="element-98">Manufacturing</option>
                  <option value="retail" data-id="element-99">Retail / E-commerce</option>
                  <option value="government" data-id="element-100">Government / Public Sector</option>
                  <option value="education" data-id="element-101">Education</option>
                  <option value="defense" data-id="element-102">Defense / Aerospace</option>
                  <option value="other" data-id="element-103">Other</option>
                </select>
              </div>
            </div>}

          {/* Notes */}
          <div data-id="element-104">
            <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-105">
              Notes
              <span className="text-astro-text-secondary/50 font-normal ml-1" data-id="element-106">
                (optional)
              </span>
            </label>
            <div className="relative" data-id="element-107">
              <FileText size={16} className="absolute left-3 top-3 text-astro-text-secondary" data-id="element-108" />

              <textarea placeholder={isInternal ? 'e.g. Staging environment for testing scan configs…' : "Any context about this client's environment…"} value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2.5 pl-9 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors resize-none" data-id="element-109" />

            </div>
          </div>

          {/* Hierarchy hint */}
          <div className="bg-[#0a0e14] border border-astro-border rounded-lg p-3" data-id="element-110">
            <p className="text-[11px] text-astro-text-secondary leading-relaxed" data-id="element-111">
              <span className="text-white font-medium" data-id="element-112">How it works:</span>{' '}
              {isInternal ? 'Your own infra is treated as a client so you get the same scanning, reports, and dashboards. Perfect for testing before rolling out to customers.' : "Your Organization → Clients (like Acme Corp) → Cloud Accounts (like their AWS or Azure subscriptions). You'll connect cloud accounts to this client next."}
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3 pt-1" data-id="element-113">
            <button onClick={handleCreateAndAddAccount} className="w-full py-2.5 bg-astro-green text-black text-sm font-bold rounded hover:bg-astro-green-dark transition-colors shadow-[0_0_15px_rgba(34,197,94,0.15)] flex items-center justify-center gap-2 group" data-id="element-114">

              {isInternal ? 'Create & Connect Your Cloud Account' : 'Create Client & Connect Cloud Account'}
              <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" data-id="element-115" />

            </button>
            <button onClick={handleCreateAndDone} className="w-full py-2.5 border border-astro-border text-sm font-medium text-astro-text-secondary rounded hover:text-white hover:border-white/30 transition-colors" data-id="element-116">

              {isInternal ? "Create — I'll add accounts later" : "Create Client — I'll add accounts later"}
            </button>
          </div>
        </div>
      </div>
    </div>;
}